import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { Colors } from 'theme';
import { scaledFormatYAxis } from 'utils/reports';
import 'components/Charts/LinesTimeZoomChart/styles.css';

const DEFAULT_MARGIN_TOP = 24;
const DEFAULT_MARGIN_BOTTOM = 72;
const DEFAULT_MARGIN_LEFT = 56;
const DEFAULT_MARGIN_RIGHT = 20;
const DEFAULT_WIDTH = 600;
const DEFAULT_HEIGHT = 360;
const FALLBACK_COLORS = [
  Colors.alizarinCrimson,
  Colors.bostonBlue,
  Colors.apple,
  Colors.affair,
  Colors.flushOrange,
  Colors.goldenFizz,
  Colors.paarl,
  Colors.persianPink,
  Colors.dustGray,
];

const parseDate = (dateValue) => {
  if (!dateValue) return null;
  if (typeof dateValue === 'string') {
    return new Date(dateValue.replace(/-/g, '/'));
  }
  return new Date(dateValue);
};

function LinesTimeZoomChart({
  data = [],
  y2Data = [],
  y2GroupName = '',
  chartMarginTop = DEFAULT_MARGIN_TOP,
  chartMarginRight = DEFAULT_MARGIN_RIGHT,
  chartMarginBottom = DEFAULT_MARGIN_BOTTOM,
  chartMarginLeft = DEFAULT_MARGIN_LEFT,
  width,
  height = DEFAULT_HEIGHT,
  yAxisTitle = '',
  y2AxisTitle = '',
  groupLines = [],
  hideLegends = false,
  yAxisMaxValue = null,
  y2AxisMaxValue = null,
  id = 'LinesTimeZoomChart',
}) {
  const theme = useTheme();
  const { chartData, groups, leftMaxValue, rightMaxValue, minTs, maxTs } = useMemo(() => {
    const allSeries = [...data, ...y2Data];
    if (!allSeries.length) {
      return {
        chartData: [],
        groups: [],
        leftMaxValue: 0,
        rightMaxValue: 0,
        minTs: null,
        maxTs: null,
      };
    }

    const rowsByTs = new Map();
    const groupSet = new Set();
    let computedLeftMax = 0;
    let computedRightMax = 0;

    allSeries.forEach((item) => {
      const date = parseDate(item.date);
      if (!date || Number.isNaN(date.getTime())) {
        return;
      }

      const timestamp = date.getTime();
      const value = Number(item.value) || 0;
      const groupName = item.group || '';
      const isRightAxis = groupName === y2GroupName;
      groupSet.add(groupName);

      if (isRightAxis) {
        computedRightMax = Math.max(computedRightMax, value);
      } else {
        computedLeftMax = Math.max(computedLeftMax, value);
      }

      if (!rowsByTs.has(timestamp)) {
        rowsByTs.set(timestamp, { timestamp, dateLabel: dayjs(date).format('MM/DD HH:mm') });
      }
      rowsByTs.get(timestamp)[groupName] = value;
    });

    const sortedRows = Array.from(rowsByTs.values()).sort((a, b) => a.timestamp - b.timestamp);
    const timestamps = sortedRows.map((row) => row.timestamp);

    return {
      chartData: sortedRows,
      groups: Array.from(groupSet),
      leftMaxValue: computedLeftMax,
      rightMaxValue: computedRightMax,
      minTs: timestamps[0] || null,
      maxTs: timestamps[timestamps.length - 1] || null,
    };
  }, [data, y2Data, y2GroupName]);

  const colorByGroup = (groupName, index) => {
    const found = groupLines.find((item) => item.group === groupName);
    if (found && found.color) {
      return found.color;
    }
    return FALLBACK_COLORS[index % FALLBACK_COLORS.length];
  };

  const lineStrokeStyle = (groupName) => {
    const groupLine = groupLines.find((item) => item.group === groupName);
    return groupLine && groupLine.isDashed ? '3 3' : '';
  };

  const useTimeLabel = minTs && maxTs ? maxTs - minTs < 2 * 24 * 60 * 60 * 1000 : false;
  const xAxisFormatter = (value) => dayjs(value).format(useTimeLabel ? 'HH:mm' : 'DD');
  const tooltipLabelFormatter = (value) => `Date: ${dayjs(value).format('MM/DD/YYYY HH:mm')}`;
  const tooltipTextColor = theme.palette.mode === 'dark' ? '#e2e8f0' : '#0f172a';
  const tooltipBgColor = theme.palette.mode === 'dark' ? '#111827' : '#ffffff';
  const tooltipBorder = theme.palette.mode === 'dark' ? '#334155' : '#d1d5db';
  const legendTextColor = theme.palette.mode === 'dark' ? '#cbd5e1' : '#475569';
  const legendFormatter = (value) => <span style={{ color: legendTextColor }}>{value}</span>;

  if (!chartData.length) {
    return (
      <div
        id={id}
        style={{
          backgroundColor: 'var(--color-bg)',
          width: width || DEFAULT_WIDTH,
          maxWidth: '100%',
          height,
        }}
        data-testid="lines-time-zoom-chart-component"
      />
    );
  }

  return (
    <div
      id={id}
      style={{
        backgroundColor: 'var(--color-bg)',
        width: width || DEFAULT_WIDTH,
        maxWidth: '100%',
        height,
      }}
      data-testid="lines-time-zoom-chart-component"
      className="chart"
    >
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={chartData}
          margin={{
            top: chartMarginTop,
            right: chartMarginRight,
            bottom: chartMarginBottom,
            left: chartMarginLeft,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            type="number"
            dataKey="timestamp"
            domain={['dataMin', 'dataMax']}
            tickFormatter={xAxisFormatter}
            allowDataOverflow
            tick={{ fill: 'var(--color-text-muted)', fontSize: 12 }}
            interval="preserveStartEnd"
            tickMargin={10}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            yAxisId="left"
            domain={[0, yAxisMaxValue || 'auto']}
            tickFormatter={(tick) => scaledFormatYAxis(yAxisMaxValue, leftMaxValue, tick)}
            tick={{ fill: 'var(--color-text-muted)', fontSize: 12 }}
            tickMargin={10}
            axisLine={false}
            tickLine={false}
            label={
              yAxisTitle
                ? {
                    value: yAxisTitle,
                    angle: -90,
                    position: 'insideLeft',
                    fill: 'var(--color-text-muted)',
                    dx: -28,
                  }
                : undefined
            }
          />
          {y2Data.length > 0 && (
            <YAxis
              yAxisId="right"
              orientation="right"
              domain={[0, y2AxisMaxValue || Math.max(rightMaxValue, 0)]}
              tick={{ fill: 'var(--color-text-muted)', fontSize: 12 }}
              tickMargin={10}
              axisLine={false}
              tickLine={false}
              label={
                y2AxisTitle
                  ? {
                      value: y2AxisTitle,
                      angle: -90,
                      position: 'insideRight',
                      fill: 'var(--color-text-muted)',
                      dx: 24,
                    }
                  : undefined
              }
            />
          )}
          <Tooltip
            labelFormatter={tooltipLabelFormatter}
            contentStyle={{
              backgroundColor: tooltipBgColor,
              border: `1px solid ${tooltipBorder}`,
              borderRadius: 6,
            }}
            labelStyle={{ color: tooltipTextColor, fontWeight: 600 }}
            itemStyle={{ color: tooltipTextColor }}
          />
          {!hideLegends && (
            <Legend verticalAlign="bottom" align="center" formatter={legendFormatter} />
          )}
          {groups.map((groupName, index) => (
            <Line
              key={groupName}
              type="linear"
              dataKey={groupName}
              yAxisId={groupName === y2GroupName ? 'right' : 'left'}
              stroke={colorByGroup(groupName, index)}
              strokeWidth={1.5}
              strokeDasharray={lineStrokeStyle(groupName)}
              dot={false}
              connectNulls
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

LinesTimeZoomChart.propTypes = {
  data: PropTypes.array.isRequired,
  y2Data: PropTypes.array,
  y2GroupName: PropTypes.string,
  chartMarginTop: PropTypes.number,
  chartMarginRight: PropTypes.number,
  chartMarginBottom: PropTypes.number,
  chartMarginLeft: PropTypes.number,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  height: PropTypes.number,
  id: PropTypes.string.isRequired,
  yAxisTitle: PropTypes.string,
  y2AxisTitle: PropTypes.string,
  groupLines: PropTypes.array,
  hideLegends: PropTypes.bool,
  yAxisMaxValue: PropTypes.number,
  y2AxisMaxValue: PropTypes.number,
};

export default LinesTimeZoomChart;
